import { z } from 'zod';
import { MAPPABLE_FIELDS, type MappedLead } from '../funnel/mapping';
import type { InboundStatus } from '../enums';
import { isCustomMappingKey } from './customization';

/** Destino de un mapeo: campo del lead o `custom.<clave>` de un campo personalizado. */
export const mappingTargetSchema = z
  .string()
  .refine(
    (k) => (MAPPABLE_FIELDS as readonly string[]).includes(k) || isCustomMappingKey(k),
    'Campo destino no válido',
  );
const fieldRecord = z.record(mappingTargetSchema, z.string().max(200)).default({});

export const inboundMappingSchema = z.object({
  companyId: z.string().uuid(),
  /** Nombre de la fuente en la URL: POST /api/v1/inbound/:source (minúsculas, guiones). */
  source: z.string().regex(/^[a-z0-9][a-z0-9-_]{1,40}$/, 'Solo minúsculas, números y guiones'),
  name: z.string().trim().min(2).max(80),
  fieldMap: fieldRecord,
  defaults: fieldRecord,
  /** Proyecto fijo cuando el payload no trae proyecto. */
  projectId: z.string().uuid().optional().nullable(),
  active: z.boolean().default(true),
});
export type InboundMappingInput = z.infer<typeof inboundMappingSchema>;
export type InboundMappingPayload = z.input<typeof inboundMappingSchema>;

export interface InboundMappingDto extends InboundMappingInput {
  id: string;
  projectName: string | null;
  updatedAt: string;
}

export interface InboundEventDto {
  id: string;
  source: string;
  externalId: string | null;
  status: InboundStatus;
  error: string | null;
  leadId: string | null;
  leadName: string | null;
  contactId: string | null;
  receivedAt: string;
  processedAt: string | null;
  attempts: number;
  importId: string | null;
  /** El contacto del lead tiene «no contactar» vigente (la ingesta no lo levantó). */
  doNotContact: boolean;
  payload: unknown;
}

/** Corrección en pantalla de una fila de importación en ERROR (`PATCH /inbound/events/:id`): se guarda y se reprocesa. */
export const inboundEventEditSchema = z.object({
  payload: z.record(z.string().max(120), z.string().max(1000)),
});
export type InboundEventEditInput = z.infer<typeof inboundEventEditSchema>;

export const inboundEventsQuerySchema = z.object({
  companyId: z.string().uuid(),
  source: z.string().optional(),
  status: z.enum(['PENDIENTE', 'PROCESADO', 'DUPLICADO', 'ERROR']).optional(),
  importId: z.string().uuid().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export interface MappingPreview {
  mapped: MappedLead;
  projectId: string | null;
  projectName: string | null;
  /** Asesor resuelto: columna "Asesor" → asesor por defecto de la importación; null = round-robin del proyecto. */
  advisorId: string | null;
  advisorName: string | null;
  identity: {
    matchType: 'DOCUMENTO' | 'TELEFONO' | 'CORREO' | null;
    contactId: string | null;
    contactName: string | null;
    /** Ya tiene un lead ACTIVO en el proyecto: se anota el recontacto en ese lead, no se crea otro. */
    activeLeadInProject: boolean;
    /** Asesor del lead activo (null = en bolsa; en ese caso la importación sí lo asigna). */
    activeLeadAdvisorName: string | null;
    /** La persona pidió no ser contactada: la fila entra sin consentimiento, sin WhatsApp ni bot. */
    doNotContact: boolean;
  };
  issues: string[];
}

export interface InboundSummary {
  companyId: string;
  bySource: Array<{
    source: string;
    total: number;
    pendientes: number;
    procesados: number;
    duplicados: number;
    errores: number;
    lastAt: string | null;
  }>;
  last24h: number;
  errors24h: number;
}
